// Controlled-vocabulary option lists used by both the Register and Edit forms.
const REGULATORY_FRAMEWORKS = [
  'HIPAA',
  'FDA SaMD',
  'ONC HTI-1',
  'NIST AI RMF',
  'EU AI Act',
  'Colorado AI Act (SB 21-169)',
  'California AB-2013',
  'Connecticut SB 1103',
  'NYC Local Law 144',
  'Illinois AIVI Act',
  'Joint Commission',
  'SOC 2',
  'ISO/IEC 42001',
];
const FDA_CLASSIFICATIONS = [
  { value: '',                          label: 'Not applicable' },
  { value: 'Not a device',              label: 'Not an FDA-regulated device' },
  { value: 'Exempt',                    label: 'Exempt' },
  { value: '510(k)',                    label: '510(k) cleared' },
  { value: 'De Novo',                   label: 'De Novo authorized' },
  { value: 'PMA',                       label: 'PMA approved' },
  { value: 'Breakthrough',              label: 'Breakthrough device designation' },
  { value: 'Pre-submission',            label: 'Pre-submission / in review' },
  { value: 'Other',                     label: 'Other' },
];

function regulatoryScopeChips(selected = [], idPrefix = 'rs') {
  const set = new Set(selected);
  return `<div class="chip-grid" id="${idPrefix}-chips">` + REGULATORY_FRAMEWORKS.map((fw) => `
    <label class="chip">
      <input type="checkbox" name="${idPrefix}-framework" value="${fw}" ${set.has(fw) ? 'checked' : ''}>
      <span class="chip-label">${fw}</span>
    </label>`).join('') + `</div>`;
}

function fdaClassificationOptions(current = '') {
  return FDA_CLASSIFICATIONS.map((c) =>
    `<option value="${c.value}" ${current === c.value ? 'selected' : ''}>${c.label}</option>`
  ).join('');
}

// Consume a pending drill-down filter set by the dashboard.
// Returns a plain object with the filter keys, or {}. Cleared after reading.
function _consumeFilter(pageKey) {
  const nf = window._navFilter;
  if (!nf || nf.page !== pageKey) return {};
  window._navFilter = null;
  return nf.filter || {};
}

export const assets = {
  async aiAssets() {
    const activeFilter = _consumeFilter('ai-assets');
    let data = { data: [], pagination: { total: 0 } };
    try {
      // "high_or_critical" maps to two API calls merged — API accepts one tier.
      if (activeFilter.risk_tier === 'high_or_critical') {
        const [h, c] = await Promise.all([
          API.getAssets({ risk_tier: 'high' }),
          API.getAssets({ risk_tier: 'critical' }),
        ]);
        data = { data: [...(c.data || []), ...(h.data || [])], pagination: { total: (c.data?.length || 0) + (h.data?.length || 0) } };
      } else {
        data = await API.getAssets(activeFilter);
      }
    } catch (e) { /* empty */ }

    const categoryLabels = {
      clinical_decision_support: 'Clinical Decision Support', diagnostic_imaging: 'Diagnostic Imaging',
      predictive_analytics: 'Predictive Analytics', nlp_extraction: 'NLP Extraction',
      operational: 'Operational', administrative: 'Administrative', revenue_cycle: 'Revenue Cycle', other: 'Other'
    };

    const filterBanner = Object.keys(activeFilter).length ? `
      <div class="filter-banner">
        <span>Showing results filtered by
          ${Object.entries(activeFilter).map(([k, v]) => `<strong>${k.replace(/_/g, ' ')}: ${v === 'high_or_critical' ? 'high or critical' : v}</strong>`).join(', ')}
        </span>
        <button class="btn btn-sm btn-ghost" data-clear-filter="ai-assets">Clear filter</button>
      </div>` : '';

    return `
      <div class="page-header">
        <div><h2>AI Asset Registry</h2><p>${data.pagination?.total ?? data.data.length} AI systems${Object.keys(activeFilter).length ? ' matching filter' : ' registered'}</p></div>
        <button class="btn btn-primary" id="btn-add-asset">+ Register AI Asset</button>
      </div>
      ${filterBanner}
      <div class="card">
        <div class="table-container">
          <table>
            <thead><tr><th>Name</th><th>Vendor</th><th>Category</th><th>Risk Tier</th><th>Status</th><th>PHI</th><th>Owner</th><th>Actions</th></tr></thead>
            <tbody>
              ${data.data.length > 0 ? data.data.map(a => `
                <tr>
                  <td><a href="#" class="asset-name-link" data-view-asset="${a.id}">${a.name}</a></td>
                  <td>${a.vendor || 'Internal'}</td>
                  <td>${categoryLabels[a.category] || a.category}</td>
                  <td>${App.badge(a.risk_tier)}</td>
                  <td>${App.badge(a.deployment_status)}</td>
                  <td>${a.phi_access ? '<span class="badge badge-high">PHI</span>' : 'No'}</td>
                  <td>${a.owner_name || 'Unassigned'}</td>
                  <td><button class="btn btn-sm btn-outline" data-view-asset="${a.id}">View</button></td>
                </tr>
              `).join('') : '<tr><td colspan="8" class="empty-state">No AI assets registered. Click "Register AI Asset" to add your first system.</td></tr>'}
            </tbody>
          </table>
        </div>
      </div>`;
  },

  viewAsset(id) {
    this._selectedAssetId = id;
    App.navigate('asset-detail');
  },

  async assetDetail() {
    const id = this._selectedAssetId;
    if (!id) return '<div class="empty-state"><p>No asset selected</p><button class="btn btn-outline" data-asset-nav="ai-assets">Back to Registry</button></div>';

    try {
      const [assetRes, riskRes, impactRes, incidentRes, metricsRes, evidenceRes] = await Promise.all([
        API.getAsset(id),
        API.getRiskAssessments({ ai_asset_id: id }),
        API.getImpactAssessments({ ai_asset_id: id }),
        API.getIncidents({ ai_asset_id: id }),
        API.getAssetMetrics(id, { limit: 20 }),
        API.getEvidence({ entity_type: 'ai_asset', entity_id: id }),
      ]);

      const a = assetRes.data;
      const risks = riskRes.data || [];
      const impacts = impactRes.data || [];
      const incidents = incidentRes.data || [];
      const metrics = metricsRes.data || [];
      const evidence = evidenceRes.data || [];

      const categoryLabels = {
        clinical_decision_support: 'Clinical Decision Support', diagnostic_imaging: 'Diagnostic Imaging',
        predictive_analytics: 'Predictive Analytics', nlp_extraction: 'NLP Extraction',
        operational: 'Operational', administrative: 'Administrative', revenue_cycle: 'Revenue Cycle', other: 'Other'
      };
      const impactLabels = {
        critical: 'Critical — life-or-death', high: 'High', moderate: 'Moderate', low: 'Low', minimal: 'Minimal'
      };
      const oversightLabels = {
        human_in_the_loop: 'Human in the loop',
        human_on_the_loop: 'Human on the loop',
        human_out_of_the_loop: 'Human out of the loop',
        advisory_only: 'Advisory only',
      };
      const sourcingLabels = {
        internal: 'Internal build', third_party: 'Third-party vendor',
        hybrid: 'Hybrid', embedded: 'Embedded', research: 'Research / pilot',
      };
      const lifecycleLabels = {
        intake: 'Intake', design: 'Design', validation: 'Validation',
        deployed: 'Deployed', monitoring: 'Monitoring', retired: 'Retired',
      };

      const parseList = (v) => { try { return JSON.parse(v || '[]'); } catch { return []; } };
      const populations = parseList(a.affected_populations);
      const regScope = parseList(a.regulatory_scope);
      const hasIntake = !!(a.business_purpose || a.decision_impact_level || a.human_oversight_mode
        || a.hosting_inference_location || populations.length || regScope.length
        || a.ai_type_classification || a.lifecycle_phase || a.prohibited_uses || a.rollback_procedure);

      return `
        <div class="asset-detail-header">
          <button class="btn btn-sm btn-outline" data-asset-nav="ai-assets">&larr; Back to Registry</button>
          <button class="btn btn-primary" data-edit-asset="${a.id}">Edit asset</button>
        </div>

        <!-- ========== Hero ========== -->
        <section class="integration-card" style="margin-bottom:var(--sp-6)">
          <header class="integration-hero">
            <div class="integration-identity">
              <div class="integration-icon integration-icon-primary" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6v6H9z"/></svg>
              </div>
              <div class="integration-title">
                <h3>${a.name}${a.version ? ` <span style="font-weight:400;color:rgba(255,255,255,0.6)">v${a.version}</span>` : ''}</h3>
                <p class="integration-subtitle">${categoryLabels[a.category] || a.category}${a.vendor ? ` · ${a.vendor}` : ' · Internal'}${a.department ? ` · ${a.department}` : ''}</p>
                <div class="integration-meta">
                  ${App.badge(a.risk_tier)}
                  ${App.badge(a.deployment_status)}
                  ${a.phi_access ? '<span class="meta-pill" style="background:rgba(185,28,28,0.22);border-color:rgba(252,165,165,0.35);color:#fecaca">PHI access</span>' : ''}
                  ${a.lifecycle_phase ? `<span class="meta-pill">Lifecycle: ${lifecycleLabels[a.lifecycle_phase] || a.lifecycle_phase}</span>` : ''}
                </div>
              </div>
            </div>
          </header>

          <!-- Quick facts strip -->
          <div class="asset-facts">
            <div class="asset-fact">
              <span class="asset-fact-label">Owner</span>
              <span class="asset-fact-value">${a.owner_name || '<em class="text-muted">Unassigned</em>'}</span>
            </div>
            <div class="asset-fact">
              <span class="asset-fact-label">Clinical champion</span>
              <span class="asset-fact-value">${a.champion_name || '<em class="text-muted">Unassigned</em>'}</span>
            </div>
            <div class="asset-fact">
              <span class="asset-fact-label">Deployment date</span>
              <span class="asset-fact-value">${a.deployment_date ? App.formatDate(a.deployment_date) : '<em class="text-muted">Not deployed</em>'}</span>
            </div>
            <div class="asset-fact">
              <span class="asset-fact-label">FDA classification</span>
              <span class="asset-fact-value">${a.fda_classification || '<em class="text-muted">Not classified</em>'}</span>
            </div>
          </div>
        </section>

        ${hasIntake ? `
        <!-- ========== Governance intake ========== -->
        <section class="integration-card" style="margin-bottom:var(--sp-6)">
          <div class="card-header">
            <h3>Governance intake</h3>
          </div>
          <div class="integration-body">
            ${a.business_purpose ? `
            <div class="field-section">
              <div class="field-section-head">
                <h4>Business purpose</h4>
                <p>What this system is for.</p>
              </div>
              <div class="intake-value">${a.business_purpose}</div>
            </div>` : ''}

            <div class="field-section">
              <div class="field-section-head">
                <h4>Decision context</h4>
                <p>Impact level, oversight model, sourcing.</p>
              </div>
              <div class="intake-grid">
                <div class="intake-item">
                  <span class="intake-label">Decision impact</span>
                  <span class="intake-value">${impactLabels[a.decision_impact_level] || '<em class="text-muted">Not set</em>'}</span>
                </div>
                <div class="intake-item">
                  <span class="intake-label">Human oversight</span>
                  <span class="intake-value">${oversightLabels[a.human_oversight_mode] || '<em class="text-muted">Not set</em>'}</span>
                </div>
                <div class="intake-item">
                  <span class="intake-label">Sourcing</span>
                  <span class="intake-value">${sourcingLabels[a.ai_type_classification] || (a.vendor ? 'Third-party vendor' : 'Internal build')}</span>
                </div>
              </div>
            </div>

            <div class="field-section">
              <div class="field-section-head">
                <h4>Scope &amp; hosting</h4>
                <p>Where this runs and who it affects.</p>
              </div>
              <div class="intake-grid">
                <div class="intake-item">
                  <span class="intake-label">Hosting / inference</span>
                  <span class="intake-value">${a.hosting_inference_location || '<em class="text-muted">Not set</em>'}</span>
                </div>
                <div class="intake-item">
                  <span class="intake-label">Affected populations</span>
                  <span class="intake-value">${populations.length ? populations.map(p => `<span class="selected-chip">${p}</span>`).join(' ') : '<em class="text-muted">Not set</em>'}</span>
                </div>
                <div class="intake-item">
                  <span class="intake-label">Regulatory scope</span>
                  <span class="intake-value">${regScope.length ? regScope.map(r => `<span class="selected-chip">${r}</span>`).join(' ') : '<em class="text-muted">Not set</em>'}</span>
                </div>
              </div>
            </div>

            ${(a.prohibited_uses || a.rollback_procedure) ? `
            <div class="field-section" style="border-bottom:none">
              <div class="field-section-head">
                <h4>Guardrails</h4>
                <p>Prohibited uses and rollback.</p>
              </div>
              <div style="display:flex;flex-direction:column;gap:var(--sp-3)">
                ${a.prohibited_uses ? `
                <div>
                  <div class="intake-label" style="margin-bottom:4px">Prohibited uses</div>
                  <div class="attestation-box" style="background:var(--danger-light);border-color:#fecaca;border-left-color:var(--danger);color:var(--danger-dark)">${a.prohibited_uses}</div>
                </div>` : ''}
                ${a.rollback_procedure ? `
                <div>
                  <div class="intake-label" style="margin-bottom:4px">Rollback procedure</div>
                  <div class="attestation-box">${a.rollback_procedure}</div>
                </div>` : ''}
              </div>
            </div>` : ''}
          </div>
        </section>` : `
        <div class="alert alert-info" style="margin-bottom:var(--sp-6)">
          <strong>Governance intake incomplete.</strong> This asset was registered before the full Phase 3 intake fields were captured, or those fields were left blank. Click <strong>Edit asset</strong> to add business purpose, decision impact, human oversight mode, hosting location, affected populations, and regulatory scope.
        </div>`}

        ${(a.description || a.intended_use || a.known_limitations) ? `
        <!-- ========== Documentation ========== -->
        <section class="card" style="margin-bottom:var(--sp-6)">
          <div class="card-header"><h3>Documentation</h3></div>
          <div class="card-body">
            ${a.description ? `<div style="margin-bottom:var(--sp-4)"><div class="detail-label">Description</div><div style="margin-top:4px">${a.description}</div></div>` : ''}
            ${a.intended_use ? `<div style="margin-bottom:var(--sp-4)"><div class="detail-label">Intended use</div><div style="margin-top:4px">${a.intended_use}</div></div>` : ''}
            ${a.known_limitations ? `<div><div class="detail-label">Known limitations</div><div style="margin-top:4px">${a.known_limitations}</div></div>` : ''}
          </div>
        </section>` : ''}

        <!-- ========== Tabs ========== -->
        <div class="tab-nav">
          <button class="tab-btn active" data-asset-tab="risk">Risk Assessments (${risks.length})</button>
          <button class="tab-btn" data-asset-tab="impact">Impact Assessments (${impacts.length})</button>
          <button class="tab-btn" data-asset-tab="incidents">Incidents (${incidents.length})</button>
          <button class="tab-btn" data-asset-tab="monitoring">Monitoring (${metrics.length})</button>
          <button class="tab-btn" data-asset-tab="evidence">Evidence (${evidence.length})</button>
        </div>

        <div class="tab-panel active" id="tab-risk">
          <div class="card">
            <div class="table-container"><table>
              <thead><tr><th>Type</th><th>Safety</th><th>Bias</th><th>Privacy</th><th>Clinical</th><th>Cyber</th><th>Regulatory</th><th>Overall</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                ${risks.length > 0 ? risks.map(r => `<tr>
                  <td>${r.assessment_type}</td>
                  <td>${r.patient_safety_score || '-'}/5</td><td>${r.bias_fairness_score || '-'}/5</td>
                  <td>${r.data_privacy_score || '-'}/5</td><td>${r.clinical_validity_score || '-'}/5</td>
                  <td>${r.cybersecurity_score || '-'}/5</td><td>${r.regulatory_score || '-'}/5</td>
                  <td>${App.badge(r.overall_risk_level)}</td><td>${App.badge(r.status)}</td>
                  <td>${App.formatDate(r.created_at)}</td>
                </tr>`).join('') : '<tr><td colspan="10" class="empty-state">No risk assessments</td></tr>'}
              </tbody>
            </table></div>
          </div>
        </div>

        <div class="tab-panel" id="tab-impact">
          <div class="card">
            <div class="table-container"><table>
              <thead><tr><th>Period</th><th>Drift Detected</th><th>Remediation</th><th>Status</th><th>Assessor</th><th>Date</th></tr></thead>
              <tbody>
                ${impacts.length > 0 ? impacts.map(ia => `<tr>
                  <td>${ia.assessment_period || 'N/A'}</td>
                  <td>${ia.drift_detected ? '<span class="badge badge-critical">Yes</span>' : '<span class="badge badge-low">No</span>'}</td>
                  <td>${ia.remediation_required ? App.badge(ia.remediation_status || 'planned') : 'Not needed'}</td>
                  <td>${App.badge(ia.status)}</td><td>${ia.assessor_name}</td>
                  <td>${App.formatDate(ia.created_at)}</td>
                </tr>`).join('') : '<tr><td colspan="6" class="empty-state">No impact assessments</td></tr>'}
              </tbody>
            </table></div>
          </div>
        </div>

        <div class="tab-panel" id="tab-incidents">
          <div class="card">
            <div class="table-container"><table>
              <thead><tr><th>Title</th><th>Type</th><th>Severity</th><th>Patient Impact</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                ${incidents.length > 0 ? incidents.map(i => `<tr>
                  <td><strong>${i.title}</strong></td><td>${i.incident_type.replace(/_/g, ' ')}</td>
                  <td>${App.badge(i.severity)}</td>
                  <td>${i.patient_impact ? '<span class="badge badge-critical">Yes</span>' : 'No'}</td>
                  <td>${App.badge(i.status)}</td><td>${App.formatDate(i.created_at)}</td>
                </tr>`).join('') : '<tr><td colspan="6" class="empty-state">No incidents reported</td></tr>'}
              </tbody>
            </table></div>
          </div>
        </div>

        <div class="tab-panel" id="tab-monitoring">
          <div class="card">
            <div class="table-container"><table>
              <thead><tr><th>Metric</th><th>Value</th><th>Min Threshold</th><th>Max Threshold</th><th>Alert</th><th>Recorded</th></tr></thead>
              <tbody>
                ${metrics.length > 0 ? metrics.map(m => `<tr>
                  <td>${m.metric_type}</td><td><strong>${m.metric_value}</strong></td>
                  <td>${m.threshold_min || '-'}</td><td>${m.threshold_max || '-'}</td>
                  <td>${m.alert_triggered ? `<span class="badge badge-${m.alert_severity}">${m.alert_severity}</span>` : '<span class="badge badge-low">OK</span>'}</td>
                  <td>${App.formatDate(m.recorded_at)}</td>
                </tr>`).join('') : '<tr><td colspan="6" class="empty-state">No monitoring data</td></tr>'}
              </tbody>
            </table></div>
          </div>
        </div>

        <div class="tab-panel" id="tab-evidence">
          <div class="card">
            <div class="card-header">
              <h3>Evidence & Documentation</h3>
              <button class="btn btn-sm btn-primary" data-add-evidence="${a.id}">+ Add Evidence</button>
            </div>
            <div class="table-container"><table>
              <thead><tr><th>Title</th><th>Type</th><th>Description</th><th>URL</th><th>Uploaded By</th><th>Date</th><th>Actions</th></tr></thead>
              <tbody>
                ${evidence.length > 0 ? evidence.map(e => `<tr>
                  <td><strong>${e.title}</strong></td><td>${App.badge(e.evidence_type)}</td>
                  <td style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${e.description || '-'}</td>
                  <td>${e.url ? `<a href="${e.url}" target="_blank" rel="noopener">Link</a>` : '-'}</td>
                  <td>${e.uploaded_by_name || 'Unknown'}</td><td>${App.formatDate(e.created_at)}</td>
                  <td><button class="btn btn-sm btn-outline" data-delete-evidence="${e.id}">Delete</button></td>
                </tr>`).join('') : '<tr><td colspan="7" class="empty-state">No evidence attached. Click "Add Evidence" to link documentation.</td></tr>'}
              </tbody>
            </table></div>
          </div>
        </div>
      `;
    } catch (err) {
      return `<div class="empty-state"><p>Error loading asset: ${err.message}</p><button class="btn btn-outline" data-asset-nav="ai-assets">Back to Registry</button></div>`;
    }
  },

  assetDetailInit() {
    // Navigation
    document.querySelectorAll('[data-asset-nav]').forEach(btn =>
      btn.addEventListener('click', () => App.navigate(btn.getAttribute('data-asset-nav'))));
    // Edit asset button
    document.querySelectorAll('[data-edit-asset]').forEach(btn =>
      btn.addEventListener('click', () => this.showEditAssetForm(btn.getAttribute('data-edit-asset'))));
    // Tab switching
    document.querySelectorAll('[data-asset-tab]').forEach(btn =>
      btn.addEventListener('click', () => this.switchAssetTab(btn.getAttribute('data-asset-tab'))));
    // Add evidence
    document.querySelectorAll('[data-add-evidence]').forEach(btn =>
      btn.addEventListener('click', () => this.showEvidenceForm('ai_asset', btn.getAttribute('data-add-evidence'))));
    // Delete evidence
    document.querySelectorAll('[data-delete-evidence]').forEach(btn =>
      btn.addEventListener('click', () => this.deleteEvidence(btn.getAttribute('data-delete-evidence'))));
  },

  switchAssetTab(tabName) {
    document.querySelectorAll('.tab-btn').forEach(btn => btn.classList.remove('active'));
    document.querySelectorAll('.tab-panel').forEach(panel => panel.classList.remove('active'));
    document.querySelector(`[data-asset-tab="${tabName}"]`)?.classList.add('active');
    document.getElementById(`tab-${tabName}`)?.classList.add('active');
  },

  async showEditAssetForm(assetId) {
    try {
      const { data: a } = await API.getAsset(assetId);
      const parseList = (v) => { try { return JSON.parse(v || '[]'); } catch { return []; } };
      const populations = parseList(a.affected_populations).join(', ');
      const regScope = parseList(a.regulatory_scope).join(', ');

      App.openModal('Edit AI Asset', `
        <form id="edit-asset-form">

          <h4 class="modal-section-head">System identity</h4>
          <div class="form-group"><label>System name <span class="required">*</span></label>
            <input type="text" id="ea-name" value="${a.name || ''}" required>
          </div>
          <div class="form-row">
            <div class="form-group"><label>Vendor</label><input type="text" id="ea-vendor" value="${a.vendor || ''}"></div>
            <div class="form-group"><label>Version</label><input type="text" id="ea-version" value="${a.version || ''}"></div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>Category</label>
              <select id="ea-category">
                <option value="clinical_decision_support" ${a.category==='clinical_decision_support'?'selected':''}>Clinical Decision Support</option>
                <option value="diagnostic_imaging" ${a.category==='diagnostic_imaging'?'selected':''}>Diagnostic Imaging</option>
                <option value="predictive_analytics" ${a.category==='predictive_analytics'?'selected':''}>Predictive Analytics</option>
                <option value="nlp_extraction" ${a.category==='nlp_extraction'?'selected':''}>NLP Extraction</option>
                <option value="operational" ${a.category==='operational'?'selected':''}>Operational</option>
                <option value="administrative" ${a.category==='administrative'?'selected':''}>Administrative</option>
                <option value="revenue_cycle" ${a.category==='revenue_cycle'?'selected':''}>Revenue Cycle</option>
                <option value="other" ${a.category==='other'?'selected':''}>Other</option>
              </select>
            </div>
            <div class="form-group"><label>Risk tier</label>
              <select id="ea-risk">
                <option value="low" ${a.risk_tier==='low'?'selected':''}>Low</option>
                <option value="moderate" ${a.risk_tier==='moderate'?'selected':''}>Moderate</option>
                <option value="high" ${a.risk_tier==='high'?'selected':''}>High</option>
                <option value="critical" ${a.risk_tier==='critical'?'selected':''}>Critical</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>Deployment status</label>
              <select id="ea-status">
                <option value="proposed" ${a.deployment_status==='proposed'?'selected':''}>Proposed</option>
                <option value="validating" ${a.deployment_status==='validating'?'selected':''}>Validating</option>
                <option value="deployed" ${a.deployment_status==='deployed'?'selected':''}>Deployed</option>
                <option value="monitoring" ${a.deployment_status==='monitoring'?'selected':''}>Monitoring</option>
                <option value="suspended" ${a.deployment_status==='suspended'?'selected':''}>Suspended</option>
                <option value="decommissioned" ${a.deployment_status==='decommissioned'?'selected':''}>Decommissioned</option>
              </select>
            </div>
            <div class="form-group"><label>Sourcing</label>
              <select id="ea-type">
                <option value="">Not set</option>
                <option value="internal" ${a.ai_type_classification==='internal'?'selected':''}>Internal build</option>
                <option value="third_party" ${a.ai_type_classification==='third_party'?'selected':''}>Third-party vendor</option>
                <option value="hybrid" ${a.ai_type_classification==='hybrid'?'selected':''}>Hybrid</option>
                <option value="embedded" ${a.ai_type_classification==='embedded'?'selected':''}>Embedded</option>
                <option value="research" ${a.ai_type_classification==='research'?'selected':''}>Research / pilot</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>Department</label><input type="text" id="ea-dept" value="${a.department || ''}"></div>
            <div class="form-group"><label>Lifecycle phase</label>
              <select id="ea-lifecycle">
                <option value="intake" ${a.lifecycle_phase==='intake'?'selected':''}>Intake</option>
                <option value="design" ${a.lifecycle_phase==='design'?'selected':''}>Design</option>
                <option value="validation" ${a.lifecycle_phase==='validation'?'selected':''}>Validation</option>
                <option value="deployed" ${a.lifecycle_phase==='deployed'?'selected':''}>Deployed</option>
                <option value="monitoring" ${a.lifecycle_phase==='monitoring'?'selected':''}>Monitoring</option>
                <option value="retired" ${a.lifecycle_phase==='retired'?'selected':''}>Retired</option>
              </select>
            </div>
          </div>

          <h4 class="modal-section-head">Governance intake</h4>
          <div class="form-group"><label>Business purpose</label>
            <textarea id="ea-purpose">${a.business_purpose || ''}</textarea>
          </div>
          <div class="form-row">
            <div class="form-group"><label>Decision impact</label>
              <select id="ea-impact">
                <option value="">Not set</option>
                <option value="critical" ${a.decision_impact_level==='critical'?'selected':''}>Critical — life-or-death</option>
                <option value="high" ${a.decision_impact_level==='high'?'selected':''}>High</option>
                <option value="moderate" ${a.decision_impact_level==='moderate'?'selected':''}>Moderate</option>
                <option value="low" ${a.decision_impact_level==='low'?'selected':''}>Low</option>
                <option value="minimal" ${a.decision_impact_level==='minimal'?'selected':''}>Minimal</option>
              </select>
            </div>
            <div class="form-group"><label>Human oversight</label>
              <select id="ea-oversight">
                <option value="">Not set</option>
                <option value="human_in_the_loop" ${a.human_oversight_mode==='human_in_the_loop'?'selected':''}>Human in the loop</option>
                <option value="human_on_the_loop" ${a.human_oversight_mode==='human_on_the_loop'?'selected':''}>Human on the loop</option>
                <option value="human_out_of_the_loop" ${a.human_oversight_mode==='human_out_of_the_loop'?'selected':''}>Human out of the loop</option>
                <option value="advisory_only" ${a.human_oversight_mode==='advisory_only'?'selected':''}>Advisory only</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>Hosting / inference location</label>
              <input type="text" id="ea-hosting" value="${a.hosting_inference_location || ''}" placeholder="e.g., us-east, on-premise">
            </div>
            <div class="form-group"><label>Affected populations</label>
              <input type="text" id="ea-populations" value="${populations}" placeholder="Comma-separated">
            </div>
          </div>
          <div class="form-group"><label>Regulatory scope</label>
            ${regulatoryScopeChips(parseList(a.regulatory_scope), 'rs-edit')}
            <small>Which frameworks apply to this system? Click to toggle.</small>
          </div>
          <div class="form-row">
            <div class="form-group"><label>FDA classification</label>
              <select id="ea-fda">${fdaClassificationOptions(a.fda_classification || '')}</select>
            </div>
            <div class="form-group"><label>Touches PHI?</label>
              <select id="ea-phi">
                <option value="0" ${!a.phi_access?'selected':''}>No</option>
                <option value="1" ${a.phi_access?'selected':''}>Yes (BAA required)</option>
              </select>
            </div>
          </div>
          <div class="form-group"><label>Description</label><textarea id="ea-desc">${a.description || ''}</textarea></div>
          <div class="form-group"><label>Intended use</label><textarea id="ea-use">${a.intended_use || ''}</textarea></div>
          <div class="form-group"><label>Known limitations</label><textarea id="ea-lim">${a.known_limitations || ''}</textarea></div>
          <div class="form-group"><label>Prohibited uses</label><textarea id="ea-prohibited">${a.prohibited_uses || ''}</textarea></div>
          <div class="form-group"><label>Rollback procedure</label><textarea id="ea-rollback">${a.rollback_procedure || ''}</textarea></div>
        </form>
      `, '<button class="btn btn-outline" data-modal-close>Cancel</button><button class="btn btn-primary" id="ea-submit">Save changes</button>');

      const splitList = (v) => v.split(',').map(s => s.trim()).filter(Boolean);
      const gatherChecked = (name) => [...document.querySelectorAll(`input[name="${name}"]:checked`)].map(el => el.value);
      document.querySelectorAll('[data-modal-close]').forEach(b => b.addEventListener('click', () => App.closeModal()));
      document.getElementById('ea-submit').addEventListener('click', async () => {
        try {
          await API.updateAsset(assetId, {
            name: document.getElementById('ea-name').value,
            vendor: document.getElementById('ea-vendor').value || null,
            version: document.getElementById('ea-version').value || null,
            category: document.getElementById('ea-category').value,
            risk_tier: document.getElementById('ea-risk').value,
            deployment_status: document.getElementById('ea-status').value,
            ai_type_classification: document.getElementById('ea-type').value || null,
            department: document.getElementById('ea-dept').value || null,
            lifecycle_phase: document.getElementById('ea-lifecycle').value || null,
            fda_classification: document.getElementById('ea-fda').value || null,
            phi_access: document.getElementById('ea-phi').value === '1',
            description: document.getElementById('ea-desc').value || null,
            intended_use: document.getElementById('ea-use').value || null,
            known_limitations: document.getElementById('ea-lim').value || null,
            // Governance intake
            business_purpose: document.getElementById('ea-purpose').value || null,
            decision_impact_level: document.getElementById('ea-impact').value || null,
            human_oversight_mode: document.getElementById('ea-oversight').value || null,
            hosting_inference_location: document.getElementById('ea-hosting').value || null,
            affected_populations: splitList(document.getElementById('ea-populations').value),
            regulatory_scope: gatherChecked('rs-edit-framework'),
            prohibited_uses: document.getElementById('ea-prohibited').value || null,
            rollback_procedure: document.getElementById('ea-rollback').value || null,
          });
          App.closeModal();
          App.toast('Asset updated', 'success');
          App.navigate('asset-detail');
        } catch (err) { App.toast(err.message, 'error'); }
      });
    } catch (err) { App.toast(err.message, 'error'); }
  },

  showEvidenceForm(entityType, entityId) {
    App.openModal('Add Evidence', `
      <form id="evidence-form">
        <div class="form-group"><label>Title *</label><input type="text" id="ev-title" required placeholder="e.g., Validation Test Results"></div>
        <div class="form-group"><label>Evidence Type</label>
          <select id="ev-type">
            <option value="document">Document</option>
            <option value="link">Link/URL</option>
            <option value="test_result">Test Result</option>
            <option value="policy">Policy</option>
            <option value="audit_report">Audit Report</option>
            <option value="certification">Certification</option>
            <option value="screenshot">Screenshot</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div class="form-group"><label>URL (optional)</label><input type="url" id="ev-url" placeholder="https://..."></div>
        <div class="form-group"><label>Description</label><textarea id="ev-desc" placeholder="Describe this evidence..."></textarea></div>
      </form>
    `, '<button class="btn btn-primary" id="ev-submit">Add Evidence</button>');
    document.getElementById('ev-submit').addEventListener('click', async () => {
      try {
        await API.createEvidence({
          entity_type: entityType,
          entity_id: entityId,
          title: document.getElementById('ev-title').value,
          evidence_type: document.getElementById('ev-type').value,
          url: document.getElementById('ev-url').value || null,
          description: document.getElementById('ev-desc').value || null,
        });
        App.closeModal();
        App.toast('Evidence added', 'success');
        if (entityType === 'ai_asset') App.navigate('asset-detail');
        else App.navigate(App.currentPage);
      } catch (err) { App.toast(err.message, 'error'); }
    });
  },

  async deleteEvidence(evidenceId) {
    if (!confirm('Delete this evidence record?')) return;
    try {
      await API.deleteEvidence(evidenceId);
      App.toast('Evidence deleted', 'success');
      App.navigate('asset-detail');
    } catch (err) { App.toast(err.message, 'error'); }
  },

  showAssetForm() {
    App.openModal('Register AI Asset', `
      <form id="asset-form">

        <!-- ========== Identity ========== -->
        <h4 class="modal-section-head">System identity</h4>
        <div class="form-group"><label>System name <span class="required">*</span></label>
          <input type="text" id="af-name" required placeholder="e.g., Sepsis Risk Scorer">
        </div>
        <div class="form-row">
          <div class="form-group"><label>Vendor</label><input type="text" id="af-vendor" placeholder="Leave blank for internal"></div>
          <div class="form-group"><label>Version</label><input type="text" id="af-version" placeholder="e.g., 1.2.0"></div>
        </div>
        <div class="form-row">
          <div class="form-group"><label>Category <span class="required">*</span></label>
            <select id="af-category">
              <option value="clinical_decision_support">Clinical Decision Support</option>
              <option value="diagnostic_imaging">Diagnostic Imaging</option>
              <option value="predictive_analytics">Predictive Analytics</option>
              <option value="nlp_extraction">NLP Extraction</option>
              <option value="operational">Operational</option>
              <option value="administrative">Administrative</option>
              <option value="revenue_cycle">Revenue Cycle</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="form-group"><label>Risk tier</label>
            <select id="af-risk">
              <option value="low">Low</option>
              <option value="moderate" selected>Moderate</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group"><label>Sourcing</label>
            <select id="af-type">
              <option value="internal">Internal build</option>
              <option value="third_party">Third-party vendor</option>
              <option value="hybrid">Hybrid (vendor + internal)</option>
              <option value="embedded">Embedded in another product</option>
              <option value="research">Research / pilot</option>
            </select>
          </div>
          <div class="form-group"><label>Department</label><input type="text" id="af-dept" placeholder="e.g., Emergency, Radiology"></div>
        </div>

        <!-- ========== Governance intake ========== -->
        <h4 class="modal-section-head">Governance intake</h4>
        <div class="form-group"><label>Business purpose</label>
          <textarea id="af-purpose" placeholder="What business or clinical problem does this AI system solve? Keep to one or two sentences."></textarea>
        </div>
        <div class="form-row">
          <div class="form-group"><label>Decision impact</label>
            <select id="af-impact">
              <option value="">Not set</option>
              <option value="critical">Critical — life-or-death / irreversible</option>
              <option value="high">High — significant clinical or financial</option>
              <option value="moderate">Moderate — affects workflow or quality</option>
              <option value="low">Low — minor operational</option>
              <option value="minimal">Minimal — informational only</option>
            </select>
          </div>
          <div class="form-group"><label>Human oversight</label>
            <select id="af-oversight">
              <option value="">Not set</option>
              <option value="human_in_the_loop">Human in the loop (clinician approves every action)</option>
              <option value="human_on_the_loop">Human on the loop (clinician monitors, can override)</option>
              <option value="human_out_of_the_loop">Human out of the loop (autonomous)</option>
              <option value="advisory_only">Advisory only (suggestion, no automated action)</option>
            </select>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group"><label>Hosting / inference location</label>
            <input type="text" id="af-hosting" placeholder="e.g., us-east, on-premise, vendor SaaS (US)">
          </div>
          <div class="form-group"><label>Affected populations</label>
            <input type="text" id="af-populations" placeholder="Comma-separated, e.g., adult inpatient, pediatric">
            <small>Who is impacted by this system's decisions?</small>
          </div>
        </div>
        <div class="form-group"><label>Regulatory scope</label>
          ${regulatoryScopeChips([], 'rs-new')}
          <small>Which frameworks apply to this system? Click to toggle.</small>
        </div>
        <div class="form-row">
          <div class="form-group"><label>FDA classification</label>
            <select id="af-fda">${fdaClassificationOptions('')}</select>
          </div>
          <div class="form-group"><label>Touches PHI?</label>
            <select id="af-phi"><option value="0">No</option><option value="1">Yes (BAA required)</option></select>
          </div>
        </div>
        <div class="form-group"><label>Description</label>
          <textarea id="af-desc" placeholder="Technical overview: data inputs, outputs, model family"></textarea>
        </div>
        <div class="form-group"><label>Intended use</label>
          <textarea id="af-use" placeholder="Clinical / operational context in which this system may be used"></textarea>
        </div>
        <div class="form-group"><label>Prohibited uses</label>
          <textarea id="af-prohibited" placeholder="Explicitly out-of-scope uses, e.g., Do not use for pediatric triage"></textarea>
        </div>
        <div class="form-group"><label>Rollback procedure</label>
          <textarea id="af-rollback" placeholder="How to disable or fall back if this system misbehaves (feature flag, manual override, vendor contact)"></textarea>
        </div>
      </form>
    `, '<button class="btn btn-outline" data-action="App.closeModal">Cancel</button><button class="btn btn-primary" id="af-submit">Register asset</button>');
    document.getElementById('af-submit').addEventListener('click', async () => {
      const splitList = (v) => v.split(',').map(s => s.trim()).filter(Boolean);
      const gatherChecked = (name) => [...document.querySelectorAll(`input[name="${name}"]:checked`)].map(el => el.value);
      try {
        await API.createAsset({
          name: document.getElementById('af-name').value,
          vendor: document.getElementById('af-vendor').value || null,
          version: document.getElementById('af-version').value || null,
          category: document.getElementById('af-category').value,
          risk_tier: document.getElementById('af-risk').value,
          ai_type_classification: document.getElementById('af-type').value || null,
          fda_classification: document.getElementById('af-fda').value || null,
          department: document.getElementById('af-dept').value || null,
          phi_access: document.getElementById('af-phi').value === '1',
          description: document.getElementById('af-desc').value || null,
          intended_use: document.getElementById('af-use').value || null,
          // Phase 3 intake
          business_purpose: document.getElementById('af-purpose').value || null,
          decision_impact_level: document.getElementById('af-impact').value || null,
          human_oversight_mode: document.getElementById('af-oversight').value || null,
          hosting_inference_location: document.getElementById('af-hosting').value || null,
          affected_populations: splitList(document.getElementById('af-populations').value),
          regulatory_scope: gatherChecked('rs-new-framework'),
          prohibited_uses: document.getElementById('af-prohibited').value || null,
          rollback_procedure: document.getElementById('af-rollback').value || null,
        });
        App.closeModal();
        App.toast('AI asset registered successfully', 'success');
        App.navigate('ai-assets');
      } catch (err) { App.toast(err.message, 'error'); }
    });
  },
};
